/**
 * platform/logs.ts — Logs & Errors requests and shapes (staff-only,
 * `platform:operations.observe`). The server builds the Logging filter from
 * these closed values and returns allowlisted, redacted rows.
 */
import type { ConsoleRequest } from './requests.js';

export const LOG_SEVERITIES = ['DEFAULT', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'] as const;
export const LOG_WINDOWS = [
  { minutes: 15, label: '15 minutes' },
  { minutes: 60, label: '1 hour' },
  { minutes: 360, label: '6 hours' },
  { minutes: 1440, label: '24 hours' },
  { minutes: 4320, label: '3 days' },
  { minutes: 10080, label: '7 days' },
] as const;

export interface LogFilter {
  readonly source: string;
  readonly minSeverity: (typeof LOG_SEVERITIES)[number];
  readonly windowMinutes: number;
  readonly requestId?: string;
  readonly q?: string;
  readonly pageToken?: string;
}

export const logRequests = {
  sources: (): ConsoleRequest => ({ method: 'GET', path: '/api/system/logs/sources' }),
  page: (f: LogFilter): ConsoleRequest => {
    const p = new URLSearchParams({
      source: f.source,
      minSeverity: f.minSeverity,
      windowMinutes: String(f.windowMinutes),
    });
    if (f.requestId) p.set('requestId', f.requestId);
    if (f.q) p.set('q', f.q);
    if (f.pageToken) p.set('pageToken', f.pageToken);
    return { method: 'GET', path: `/api/system/logs?${p.toString()}` };
  },
};

export type LogSourceStatus = 'connected' | 'not_connected' | 'not_captured' | 'not_deployed';
export interface LogSourcesRead {
  readonly readable: boolean;
  readonly sources: readonly {
    readonly id: string;
    readonly label: string;
    readonly status: LogSourceStatus;
    readonly note: string;
  }[];
}
export interface LogRow {
  readonly id: string;
  readonly timestamp: string;
  readonly severity: string;
  readonly service: string | null;
  readonly requestId: string | null;
  readonly traceId: string | null;
  readonly message: string | null;
  readonly withheld: boolean;
  readonly fields: Readonly<Record<string, string | number | boolean>>;
  readonly http: { method: string; status: number; path: string; latency: string | null } | null;
}
export interface LogPageRead {
  readonly source: string;
  readonly state:
    'connected' | 'not_configured' | 'permission_denied' | 'unavailable' | LogSourceStatus;
  readonly note?: string;
  readonly entries: readonly LogRow[];
  readonly nextPageToken?: string | null;
  readonly newestAt?: string | null;
  readonly measuredAt?: string;
}

/** Errors per hour across a page, oldest first. Labelled as "this page", never a total. */
export function hourlyErrorTrend(rows: readonly LogRow[]): { hour: string; errors: number }[] {
  const buckets = new Map<string, number>();
  for (const r of rows) {
    if (!['ERROR', 'CRITICAL', 'ALERT', 'EMERGENCY'].includes(r.severity)) continue;
    const hour = r.timestamp.slice(0, 13);
    buckets.set(hour, (buckets.get(hour) ?? 0) + 1);
  }
  return [...buckets.entries()].sort().map(([hour, errors]) => ({ hour, errors }));
}

/** Every request shape this module builds — walked by the nginx binding test. */
export function allLogRequestShapes(): ConsoleRequest[] {
  return [
    logRequests.sources(),
    logRequests.page({ source: 'api', minSeverity: 'ERROR', windowMinutes: 60 }),
  ];
}
